import type { Repository } from "@octopusdeploy/octopus-server-client";
import React from "react";
import { type ReactElement } from "react";
import { AddSpaceWorkflow, addSpaceWorkflowLoader } from "~/areas/configuration/components/Spaces/AddSpaceWorkflow";
import type { DialogWorkflow, DrawerWorkflow, Workflow } from "~/workflows/workflows";
import { workflows } from "~/workflows/workflows";
const workflowRegistrations = {
    addSpaceWorkflow: createDialogWorkflowRegistration({
        workflow: workflows.addSpaceWorkflow,
        loader: (repository) => addSpaceWorkflowLoader(repository),
        render: ({ completeWorkflow, loaderData }) => <AddSpaceWorkflow loaderData={loaderData} onSpaceCreated={completeWorkflow}/>,
    }),
};
export type WorkflowRegistration<Input, Output, LoaderData> = DialogWorkflowRegistration<Input, Output, LoaderData> | DrawerWorkflowRegistration<Input, Output, LoaderData>;
export interface DialogWorkflowRegistration<Input, Output, LoaderData> {
    workflow: DialogWorkflow<Input, Output>;
    loader: WorkflowLoader<Input, LoaderData> | undefined;
    render: (props: WorkflowRenderProps<Input, Output, LoaderData | null>) => ReactElement;
}
export interface DrawerWorkflowRegistration<Input, Output, LoaderData> {
    workflow: DrawerWorkflow<Input, Output>;
    loader: WorkflowLoader<Input, LoaderData> | undefined;
    loadingTitle: string;
    primaryButtonLabel: string;
    render: (props: WorkflowRenderProps<Input, Output, LoaderData>) => ReactElement;
}
export function isDialogWorkflowRegistration<Input, Output, LoaderData>(workflowRegistration: WorkflowRegistration<Input, Output, LoaderData>): workflowRegistration is DialogWorkflowRegistration<Input, Output, LoaderData> {
    return workflowRegistration.workflow.workflowType === "dialog";
}
interface DrawerWorkflowConfiguration<Input, Output, LoaderData> {
    workflow: DrawerWorkflow<Input, Output>;
    loader?: WorkflowLoader<Input, LoaderData>;
    loadingTitle: string;
    primaryButtonLabel: string;
    render: (props: WorkflowRenderProps<Input, Output, LoaderData>) => ReactElement;
}
interface DialogWorkflowConfiguration<Input, Output, LoaderData> {
    workflow: DialogWorkflow<Input, Output>;
    loader?: WorkflowLoader<Input, LoaderData>;
    render: (props: WorkflowRenderProps<Input, Output, LoaderData | null>) => ReactElement;
}
function createDialogWorkflowRegistration<Input, Output, LoaderData>({ workflow, loader, render }: DialogWorkflowConfiguration<Input, Output, LoaderData>): DialogWorkflowRegistration<Input, Output, LoaderData> {
    return {
        workflow,
        loader,
        render,
    };
}
function createDrawerWorkflowRegistration<Input, Output, LoaderData>({ workflow, loader, loadingTitle, render, primaryButtonLabel }: DrawerWorkflowConfiguration<Input, Output, LoaderData>): DrawerWorkflowRegistration<Input, Output, LoaderData> {
    return {
        workflow,
        loader,
        loadingTitle,
        render,
        primaryButtonLabel,
    };
}
interface WorkflowRenderProps<Input, Output, LoaderData> {
    input: Input;
    loaderData: LoaderData;
    completeWorkflow: (output: Output) => void;
}
export type WorkflowLoader<Input, LoaderData> = (repository: Repository, parameters: {
    input: Input;
}) => Promise<LoaderData>;
export function getWorkflowRegistrationForWorkflow<Input, Output, LoaderData>(workflow: Workflow<Input, Output>): WorkflowRegistration<Input, Output, LoaderData> | null {
    const workflowRegistration = Object.values(workflowRegistrations).find((w) => w.workflow.workflowId === workflow.workflowId);
    if (workflowRegistration === undefined) {
        return null;
    }
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return workflowRegistration as unknown as WorkflowRegistration<Input, Output, LoaderData>;
}
